import { useSelector } from "react-redux";
import "./AppPayEnroll.css";
import { selectCurrentWorkspace, selectUIStates, selectUser, setUser, setUIStates } from "../../../redux/features/appData";
import { useEffect, useState, useMemo } from "react";
import { Company, Workspace, Address, User, UserExtended, CheckCompany, CheckSignatory, isCheckError, CheckResponseError, isValidURL, isValidPostalCode, isValidPhone, formatPhoneNumber, isValidFederalEIN, formatFederalEIN, CheckBankAccount, CheckBankAccountRawType, CheckBusinessType, CHECK_BUSINESS_TYPES, CheckTaxParameter, CheckOnboardingIssue } from "winslow-core";
import { apiCreateCompany, apiGetCompany, apiUpdateCompanyAsOnboarded } from "../../../services/CompanyService";
import { apiUpdateWorkspaceCheckOnboarded } from "../../../api";
import { apiGetUser, apiGetWorkspace } from "../../../api";
import { useDispatch } from "react-redux";
import { AppChanges } from "../app-changes/AppChanges";
import "../app-time-off/AppCompanyHolidays.css";
import { AppAddress } from "../app-address/AppAddress";
import { Button, Label } from "../../../ds2/components";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { Navigate, useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
    apiCheckGetCompany,
    apiCheckGetSignatories,
    apiCheckGetCompanyOnboardLink,
    apiCheckUpdateFederalEIN,
    apiCheckGetTaxParameters,
    apiCheckUpdateTaxParameter,
    apiCheckRenderForm,
    apiCheckStartImplementation
} from "../../../services/PayrollService";
import { CheckEmbeddedComponentV2 } from "../../payroll/CheckEmbeddedComponentV2";
// import { CheckEmbeddedComponent } from "../../payroll/CheckEmbeddedComponent";
import { AppCheckErrorMessage } from "./AppCheckErrorMessage";
import { AppPaySignatoryNew } from "./AppPaySignatoryNew";
import { AppPayBankAccounts } from "./AppPayBankAccounts";
import { AppPayBankAccount } from "./AppPayBankAccount";
import { ModalTaxParameterEdit } from "./ModalTaxParameterEdit";
import { ModalCheckFormEditor } from "./ModalCheckFormEditor";
import { ModalCheckFormViewer } from "./ModalCheckFormViewer";
import { ModalCheckCompanyFiling } from "./ModalCheckCompanyFiling";
import { AppPayBankAccountAuthorization } from "./AppPayBankAccountAuthorization";
import { AppPayFilingAuthorization } from "./AppPayFilingAuthorization";
import { AppPayTaxParameterSetup } from "./AppPayTaxParameterSetup";

type AppPayEnrollProps = {
};

export const AppPayEnroll = ({}: AppPayEnrollProps) => {
    const dispatch = useDispatch(); 
    const navigate = useNavigate();
    
    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);

    const [workspace, setWorkspace] = useState<Workspace | null>(null);
    const [checkCompany, setCheckCompany] = useState<CheckCompany | null>(null);
    const [checkSignatories, setCheckSignatories] = useState<CheckSignatory[] | null>(null);
    const [checkOnboardLink, setCheckOnboardLink] = useState<string | null>(null);
    const [checkTaxParameters, setCheckTaxParameters] = useState<CheckTaxParameter[] | null>(null);
    const [loadingTaxParameters, setLoadingTaxParameters] = useState<boolean>(false);
    const [editingTaxParameter, setEditingTaxParameter] = useState<CheckTaxParameter | null>(null);

    const [loadingFilingAuthorization, setLoadingFilingAuthorization] = useState<boolean>(false);
    const [editingFilingAuthorization, setEditingFilingAuthorization] = useState<CheckOnboardingIssue | null>(null);
    const [viewingFilingAuthorization, setViewingFilingAuthorization] = useState<CheckOnboardingIssue | null>(null);
    const [embeddingFilingAuthorization, setEmbeddingFilingAuthorization] = useState<boolean>(false);

    const [newCompany, setNewCompany] = useState<Partial<Company>>({
        workspaceId: workspace?.id || "",
        legalName: "",
        tradeName: "",
        website: "",
        checkCompanyId: "",
    });

    const [address, setAddress] = useState<Address>({
        line1: "",
        line2: "",
        postal_code: "",
        city: "",
        state: "",
        country: "",
    });

    const [firstPayrollDateCF, setFirstPayrollDateCF] = useState<Date | null>(null);
    const [businessType, setBusinessType] = useState<CheckBusinessType | null>(null);
    const [phone, setPhone] = useState<string>("");
    const [federalEIN, setFederalEIN] = useState<string>("");

    const [isProcessing, setIsProcessing] = useState(false);
    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    const [loadingCheckCompany, setLoadingCheckCompany] = useState(false);

    const [isLaunching, setIsLaunching] = useState(false);
    const [implementationFailed, setImplementationFailed] = useState(false);

    useEffect(() => {
        dispatch(setUIStates({...uiStates, hrisLocation: "Payroll Enrollment"}));
    }, []);

    useEffect(() => {
        (async () => {
            if (workspace?.company?.id) {
                const company = await apiGetCompany(workspace.company.id);
                if (company) {
                    setNewCompany((prev) => ({ ...prev, workspaceId: workspace.id }));
                }
            }
        })();
    }, [workspace]);

    useEffect(() => {
        if (storeUser?.currentWorkspaceId) {
            const theWorkspace = storeUser.workspaces.find((w: Workspace) => w.id === storeUser.currentWorkspaceId) ?? null;
            setWorkspace(theWorkspace);

            setLoadingCheckCompany(true);

            (async () => {

                // we might have started the process but not be done (like signatories)
                // in which case we need the company that was created to be in memory
                if (theWorkspace?.company?.id) {
                    const company = await apiGetCompany(workspace?.company?.id || "");
                    if (company) {
                        setNewCompany(company);
                    }
                }

                if (theWorkspace?.company?.checkCompanyId) {
                    const [apiResponseCompany, apiResponseSignatories, apiResponseTaxParameters] = await Promise.all([
                        apiCheckGetCompany(theWorkspace?.company?.checkCompanyId || ""),
                        apiCheckGetSignatories(theWorkspace?.company?.checkCompanyId || ""),
                        apiCheckGetTaxParameters(theWorkspace?.company?.checkCompanyId || "")
                    ]);

                    if (!apiResponseCompany || !apiResponseSignatories || !apiResponseTaxParameters) {
                        toast("Error loading enrollment data", toastFail);
                        setCheckCompany(null);
                        setCheckSignatories(null);
                        return;
                    }

                    const checkCompany = apiResponseCompany?.data;
                    const checkSignatories = apiResponseSignatories?.data;
                    const checkTaxParameters = apiResponseTaxParameters?.data;
                    
                    if (!checkCompany || !checkSignatories || !checkTaxParameters) {
                        toast("Error loading payroll company", toastFail);
                        setCheckCompany(null);
                        setCheckSignatories(null);
                        setCheckTaxParameters(null);
                    } else {
                        setCheckCompany(checkCompany);
                        setCheckSignatories(checkSignatories);
                        setCheckTaxParameters(checkTaxParameters);

                        const ein = checkTaxParameters.find(taxParameter => taxParameter.name === "federal_ein")?.setting?.value || "";
                        setFederalEIN(ein);
                    }
                } else {
                    setCheckCompany(null);
                    setCheckSignatories(null)
                    setCheckTaxParameters(null);
                }

                // if we have both, get the onboarding link
                if (checkCompany && checkSignatories && checkSignatories.length > 0) {
                    const apiResponse = await apiCheckGetCompanyOnboardLink(checkCompany.id, checkSignatories[0].id);
                    const onboardingLink = apiResponse?.data;
                    setCheckResponseError(apiResponse?.checkResponseError);

                    if (!apiResponse || isCheckError(apiResponse) || !onboardingLink) {
                        toast("Error loading onboarding link", toastFail);
                        return;
                    }

                    // alert("[AppPayEnroll] onboardingLink: " + onboardingLink);
                    setCheckOnboardLink(onboardingLink);
                }

                setLoadingCheckCompany(false)

                // console.log("[AppPayEnroll] checkCompany: ", checkCompany);
                // console.log("[AppPayEnroll] workspace?: ", workspace);

                // now check if we have a check company status complete and
                // the winslow company is not
                if (checkCompany?.onboard && 
                    checkCompany.onboard.status === "completed" && 
                    !workspace?.checkOnboarded) {
                        // return;
                        // update the winslow company with the check company status
                        toast("Finalizing onboarding for company", toastSuccess);
                        const updatedCompany = await apiUpdateCompanyAsOnboarded(workspace?.company?.id || "");
                        if (!updatedCompany) {
                           toast("Error finalizing onboarding for company", toastFail);
                        } else {
                            const updatedWorkspace = await apiGetWorkspace(workspace?.id || "");

                            if (updatedWorkspace) {
                                setWorkspace(updatedWorkspace);
                            }
                        }
                }        
            })();
        }
    }, [storeUser, workspace]);
    
    const formatDateToYYYYMMDD = (date: Date | null): string => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const isDateTodayOrLater = (date: Date | null): boolean => {
        if (!date) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0);
        return compareDate >= today;
    };

    const getLastDayOfMonth = (date: Date): Date => {
        const year = date.getFullYear();
        const month = date.getMonth();
        return new Date(year, month + 1, 0);
    };

    const isFederalHoliday = (date: Date): boolean => {
        const month = date.getMonth(); // 0-11
        const day = date.getDate();
        
        // New Year's Day: January 1
        if (month === 0 && day === 1) return true;
        // Juneteenth National Independence Day: June 19
        if (month === 5 && day === 19) return true;
        // Independence Day: July 4
        if (month === 6 && day === 4) return true;
        // Veterans Day: November 11
        if (month === 10 && day === 11) return true;
        // Christmas Day: December 25
        if (month === 11 && day === 25) return true;
        
        return false;
    };

    const getAdjustedDate = (date: Date): Date => {
        // Find the first previous weekday that is not a holiday
        let adjusted = new Date(date);
        
        // Keep going back until we find a weekday that's not a holiday
        while (adjusted.getDay() === 0 || adjusted.getDay() === 6 || isFederalHoliday(adjusted)) {
            adjusted.setDate(adjusted.getDate() - 1);
        }
        
        return adjusted;
    };

    const isValidPayrollStartDate = (date: Date | null): boolean => {
        if (!date) return false;
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        
        // Cannot be before today
        if (checkDate < today) {
            return false;
        }

        const year = checkDate.getFullYear();
        const month = checkDate.getMonth();
        
        // Calculate the valid date for the 15th (adjusted if weekend)
        const fifteenth = new Date(year, month, 15);
        const adjustedFifteenth = getAdjustedDate(fifteenth);
        adjustedFifteenth.setHours(0, 0, 0, 0);
        
        // Calculate the valid date for the last day of the month (adjusted if weekend)
        const lastDayOfMonth = getLastDayOfMonth(checkDate);
        const adjustedLastDay = getAdjustedDate(lastDayOfMonth);
        adjustedLastDay.setHours(0, 0, 0, 0);
        
        // Check if the selected date matches either valid date
        return checkDate.getTime() === adjustedFifteenth.getTime() || 
               checkDate.getTime() === adjustedLastDay.getTime();
    };

    const filterDate = (date: Date): boolean => {
        if (!isDateTodayOrLater(date)) {
            return false;
        }
        return isValidPayrollStartDate(date);
    };

    const handleCreateCompany = async () => {
        if (!businessType) {
            toast("Please select a business type", toastFail);
            return;
        }

        if (!phone || !isValidPhone(phone.trim())) {
            toast("Please enter a valid phone number", toastFail);
            return;
        }

        if (!federalEIN || !isValidFederalEIN(federalEIN.trim())) {
            toast("Please enter a valid Federal EIN", toastFail);
            return;
        }

        setCheckResponseError(undefined);
        setIsProcessing(true);

        const firstPayrollDateCFFormatted = formatDateToYYYYMMDD(firstPayrollDateCF);

        const companyResponse = await apiCreateCompany(
            {
                legalName: newCompany.legalName?.trim() || "", 
                tradeName: newCompany.tradeName?.trim() || "",
                website: newCompany.website?.trim() || "",
                workspaceId: workspace?.id || "",
                address: JSON.stringify(address),
                firstPayrollDateCF: firstPayrollDateCFFormatted || "",
                additionalInfo: {
                    business_type: businessType,
                    phone: phone.replace(/\D/g, ""),
                    federal_ein: federalEIN.trim(),
                },
            });

        setCheckResponseError(companyResponse.checkResponseError ?? undefined);

        // Store firstPayrollDateCF in the format YYYY-MM-DD
        // console.log("[AppPayEnroll] firstPayrollDateCF:", firstPayrollDateCFFormatted);

        if (!companyResponse.response) {
            toast("Error creating company", toastFail);
            setIsProcessing(false);
            return;
        } else if (companyResponse.checkResponseError) {
            toast("Syncing error", toastFail);
            setCheckResponseError(companyResponse.checkResponseError);
            setIsProcessing(false);
            return;
        }

        const createdCompany = companyResponse.response;
        const checkCompanyId = createdCompany?.checkCompanyId;

        // Update Federal EIN in Check after successful company creation
        if (checkCompanyId && federalEIN.trim()) {
            try {
                const einResponse = await apiCheckUpdateFederalEIN(checkCompanyId, federalEIN.trim());
                if (!einResponse || isCheckError(einResponse)) {
                    console.error("[AppPayEnroll] Failed to update Federal EIN in Check:", einResponse?.checkResponseError);
                    toast("Company created but failed to update Federal EIN", toastFail);
                } else {
                    console.log("[AppPayEnroll] Federal EIN updated successfully in Check");
                }
            } catch (error) {
                console.error("[AppPayEnroll] Error updating Federal EIN:", error);
                toast("Company created but failed to update Federal EIN", toastFail);
            }
        }

        toast("Company created successfully", toastSuccess);
           
        // cleanup redux state which should trigger a rerender of the page
        const updatedUser: UserExtended| null = await apiGetUser(storeUser?.id || "");

        // console.log("[AppPayEnroll] updatedUser: ", updatedUser);

        if (updatedUser) {
            dispatch(setUser(updatedUser));
            setWorkspace(updatedUser?.workspaces.find((w: Workspace) => w.id === updatedUser.currentWorkspaceId) ?? null);
        }

        setIsProcessing(false);
    }

    const handleSignatoryCreated = async (signatories: CheckSignatory[]) => {
        setCheckSignatories(signatories);
        
        // If we have signatories, get the onboarding link
        if (checkCompany && signatories.length > 0) {
            const apiResponse = await apiCheckGetCompanyOnboardLink(checkCompany.id, signatories[0].id);
            const onboardingLink = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !onboardingLink) {
                toast("Error loading onboarding link", toastFail);
                return;
            }

            setCheckOnboardLink(onboardingLink);
        }
    };

    const handleSignatoryError = (error: CheckResponseError | undefined) => {
        setCheckResponseError(error);
    };

    const handleStartImplementation = async () => {
        if (!checkCompany?.id) {
            toast("Company id not found", toastFail);
            return;
        }

        if (implementationFailed) {
            toast("Implementation failed previously, please reload page", toastFail);
            return;
        }

        const apiResponse = await apiCheckStartImplementation(checkCompany.id);
        setCheckResponseError(apiResponse?.checkResponseError);

        if (apiResponse?.checkResponseError) {
            toast("Error starting implementation", toastFail);
            return;
        }

        // Reload checkCompany after successful start implementation
        if (workspace?.company?.checkCompanyId) {
            try {
                const apiResponseCompany = await apiCheckGetCompany(workspace.company.checkCompanyId);
                if (apiResponseCompany?.data) {
                    setCheckCompany(apiResponseCompany.data);
                    if (apiResponseCompany?.data?.implementation?.status === "needs_attention") {
                        setImplementationFailed(true);
                    }
                }

            } catch (error) {
                console.error("[AppPayEnroll] Error reloading checkCompany after start implementation:", error);
            }

            
        }
    }

    const handleLaunch = async () => {
        // console.log("[AppPayEnroll] handleLaunch, state: ", state);
        // if (!!true) {
        //     return;
        // };

        if (!newCompany?.id ) {
            toast("Company id not found", toastFail);
            console.log("[AppPayEnroll] newCompany: ", newCompany);
            return;
        }

        try {
            setIsLaunching(true);
            const apiResponse = await apiCheckGetCompanyOnboardLink(checkCompany?.id || "", checkSignatories?.[0]?.id || "");
            const link = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !link) {
                toast("Error getting onboarding link", toastFail);
                return;
            }

            if (!link) {
                toast("Error getting onboarding link", toastFail);
                return;
            }

            // update the workspace onboarding status
            const updatedWorkspace = await apiUpdateWorkspaceCheckOnboarded(workspace?.id || "", true);
            if (!updatedWorkspace) {
                toast("Error updating workspace onboarding status", toastFail);
                return;
            }

            // get new workspaces loaded into redux
            const updatedUser: UserExtended | null = await apiGetUser(storeUser?.id || "");
            if (updatedUser) {
                dispatch(setUser(updatedUser));
            }

            toast("Launching onboarding... ", toastSuccess);
            setCheckOnboardLink(link);
            console.log("[AppPayEnroll] link: ", link);
        } catch (error) {
            console.error("[AppPayEnroll] Error launching onboarding:", error);
            toast("Error launching onboarding", toastFail);
            setIsLaunching(false);
        } finally {
            setIsLaunching(false);
        }
    }

    const handleCreateBankAccount = async (bankAccount: CheckBankAccount) => {
        const bankAccountId = bankAccount.id || "";
        // console.log("[AppPayEnroll] bankAccount created: ", bankAccount);
        const bank_accounts = [bankAccountId]
        setCheckCompany((prev) => { return prev ? { ...prev, bank_accounts } : null })
    }

    const handleSaveTaxParameter = async (taxParameter: CheckTaxParameter, value: string): Promise<{ success: boolean; error?: CheckResponseError }> => {
        if (!checkCompany?.id) {
            return {
                success: false,
                error: {
                    status: 400,
                    data: null,
                    errorMessage: "Company not found",
                },
            };
        }

        // console.log("[AppPayEnroll] Saving tax parameter:", taxParameter.name, value);
        
        const apiResponse = await apiCheckUpdateTaxParameter(checkCompany.id, taxParameter.id, value);
        
        if (!apiResponse) {
            return {
                success: false,
                error: {
                    status: 500,
                    data: null,
                    errorMessage: "No response from server",
                },
            };
        }
        
        if (apiResponse.error || isCheckError(apiResponse)) {
            console.error("[AppPayEnroll] Error updating tax parameter:", apiResponse);
            return {
                success: false,
                error: apiResponse.checkResponseError || {
                    status: 500,
                    data: null,
                    errorMessage: "Failed to update tax parameter",
                },
            };
        }

        if (workspace?.company?.checkCompanyId) {
            const apiResponseCompany = await apiCheckGetCompany(workspace.company.checkCompanyId);
            if (apiResponseCompany?.data) {
                setCheckCompany(apiResponseCompany.data);
            }
        }

        // Refresh tax parameters after successful update
        if (checkCompany.id) {
            const taxParamsResponse = await apiCheckGetTaxParameters(checkCompany.id);
            if (taxParamsResponse?.data) {
                setCheckTaxParameters(taxParamsResponse.data);
            }
        }

        return { success: true };
    };

    const handleOpenForm = (filingAuthorization: CheckOnboardingIssue) => {
        setEditingFilingAuthorization(filingAuthorization);
    };

    const handleCloseFormModal = () => {
        setEditingFilingAuthorization(null);
    };

    const handleFormSubmitSuccess = async () => {
        // Refresh company data to get updated form status
        if (checkCompany?.id) {
            const apiResponseCompany = await apiCheckGetCompany(checkCompany.id);
            if (apiResponseCompany?.data) {
                setCheckCompany(apiResponseCompany.data);
            }
        }
    };

    const handleViewForm = (filingAuthorization: CheckOnboardingIssue) => {
        setViewingFilingAuthorization(filingAuthorization);
    };

    const handleCloseViewFormModal = () => {
        setViewingFilingAuthorization(null);
    };

    const handleEmbedFilingAuthorization = () => {
        setEmbeddingFilingAuthorization(true);
    };

    const handleCloseEmbedModal = async () => {
        setEmbeddingFilingAuthorization(false);
        
        // Refresh checkCompany and related data when modal closes
        if (workspace?.company?.checkCompanyId) {
            try {
                const [apiResponseCompany, apiResponseSignatories, apiResponseTaxParameters] = await Promise.all([
                    apiCheckGetCompany(workspace.company.checkCompanyId),
                    apiCheckGetSignatories(workspace.company.checkCompanyId),
                    apiCheckGetTaxParameters(workspace.company.checkCompanyId)
                ]);

                if (apiResponseCompany?.data) {
                    setCheckCompany(apiResponseCompany.data);
                }
                if (apiResponseSignatories?.data) {
                    setCheckSignatories(apiResponseSignatories.data);
                }
                if (apiResponseTaxParameters?.data) {
                    setCheckTaxParameters(apiResponseTaxParameters.data);
                    
                    // Update Federal EIN if it changed
                    const ein = apiResponseTaxParameters.data.find(taxParameter => taxParameter.name === "federal_ein")?.setting?.value || "";
                    if (ein) {
                        setFederalEIN(ein);
                    }
                }

                // Refresh onboarding link if we have signatories
                const updatedCheckCompany = apiResponseCompany?.data;
                const updatedCheckSignatories = apiResponseSignatories?.data;
                if (updatedCheckCompany && updatedCheckSignatories && updatedCheckSignatories.length > 0) {
                    const apiResponse = await apiCheckGetCompanyOnboardLink(updatedCheckCompany.id, updatedCheckSignatories[0].id);
                    const onboardingLink = apiResponse?.data;
                    if (onboardingLink) {
                        setCheckOnboardLink(onboardingLink);
                    }
                }
            } catch (error) {
                console.error("[AppPayEnroll] Error refreshing data after embed modal close:", error);
            }
        }
    };

    const handleFilingAuthorizationComplete = async () => {
        // Reload checkCompany when filing authorization completes
        if (workspace?.company?.checkCompanyId) {
            try {
                const apiResponseCompany = await apiCheckGetCompany(workspace.company.checkCompanyId);
                if (apiResponseCompany?.data) {
                    setCheckCompany(apiResponseCompany.data);
                }
            } catch (error) {
                console.error("[AppPayEnroll] Error reloading checkCompany after filing authorization complete:", error);
            }
        }
    };

    // loading, one of the check statuses, or new w/o signatories or w/ them
    console.log("[AppPayEnroll] checkCompany: ", checkCompany);
    console.log("[AppPayEnroll] checkSignatories: ", checkSignatories);
    console.log("[AppPayEnroll] check bank accounts: ", checkCompany?.bank_accounts);
    console.log("[AppPayEnroll] checkOnboardLink: ", checkOnboardLink);
    console.log("[AppPayEnroll] checkTaxParameters: ", checkTaxParameters);

    const isBankAuthorization = checkCompany?.onboard?.bank_account && 
        checkCompany?.onboard?.bank_account?.length > 0 && 
        checkCompany?.onboard?.bank_account[0] === "bank_account_debit_authorization_submitted";

    const readyToImplement = false;

    const state = 
      loadingCheckCompany ? "loading" : 
        !checkCompany ? "new" :     
            (checkCompany?.onboard?.status === "completed" && readyToImplement) ? "implementation-ready" :
                (checkCompany?.onboard?.status === "completed") ? "onboarded" :
                    (checkCompany.bank_accounts.length < 1) ? "bank-accounts" :
                        (checkSignatories && checkSignatories.length < 1) ? "signatories" : 
                            isBankAuthorization ? "bank-authorization" :
                            ((checkCompany?.onboard?.setup_parameters ?? []).length > 0) ? "setup-parameters" :
                                ((checkCompany?.onboard?.filing_authorization ?? []).length > 0) ? "filing-authorization" :
                                    checkOnboardLink ? "onboarding" : 
                                        "launch";

    console.log("[AppPayEnroll] ONBOARD STATE: ", state);

    // if we are onboarded, redirect to the pay config page
    if (state === "onboarded") {
        navigate("/app/pay-configuration");
        return null
    }

    if (state === "launch" && !isLaunching) {
        handleLaunch();
        return null;
    }

    if (state === "implementation-ready") {
        handleStartImplementation();
        return null;
    }

    // console.log("[AppPayEnroll] workspace: ", workspace);
    console.log("[AppPayEnroll] checkCompany: ", checkCompany);
    // console.log("[AppPayEnroll] checkCompany.bank_accounts: ", checkCompany?.bank_accounts);
    // console.log("[AppPayEnroll] checkTaxParameters: ", checkTaxParameters);
    // console.log("[AppPayEnroll] checkSignatories: ", checkSignatories);
    // console.log("[AppPayEnroll] checkOnboardLink: ", checkOnboardLink);
    // console.log("[AppPayEnroll] state: ", state);
    // console.log("[AppPayEnroll] newCompany: ", newCompany);
    // console.log("[AppPayEnroll] isValidURL(newCompany.website): ", isValidURL(newCompany.website || ""));

    return (
        <div className="app-pay-enroll">
            {state === "loading" &&
                <div className="app-pay-enroll-new-contents">
                    <div className="app-pay-enroll-new-left">
                        <Label>
                            Loading ...
                        </Label>
                    </div>
                </div>
            }

            {checkResponseError && 
                <AppCheckErrorMessage
                    checkResponseError={checkResponseError}
                />
            }               

            {/* {state !== "loading" && state !== "new" && state !== "signatories" &&
                <div className="app-pay-enroll-onboarding-state">
                    Onboarrding state: {state}
                </div>
            } */}
            
            {state === "new" &&
                <div className="app-pay-enroll-new-contents">
                    <div className="app-pay-enroll-new-left">
                        <Label required>
                            Legal Name
                        </Label>
                        <div className="app-custom-property-value">
                            <input 
                                type="text"
                                placeholder="Enter legal company name"
                                value={newCompany.legalName || ""}
                                onChange={(e) => {
                                    setNewCompany({ ...newCompany, legalName: e.target.value });
                                }}
                            />
                        </div>

                        <Label>
                            Trade Name
                        </Label>
                        <div className="app-custom-property-value">
                            <input 
                                type="text"
                                placeholder="Enter trade name (optional)"
                                value={newCompany.tradeName || ""}
                                onChange={(e) => {
                                    setNewCompany({ ...newCompany, tradeName: e.target.value });
                                }}
                            />
                        </div>

                        <div style={{ display: "flex", gap: "16px" }}>
                            <div style={{ flex: 1 }}>
                                <Label required>
                                    Business Type
                                </Label>
                                <div className="app-custom-property-value">
                                    <GenericSelect
                                        options={[
                                            {
                                                displayName: "Select Business Type",
                                                value: "",
                                            },
                                            ...CHECK_BUSINESS_TYPES.map((type) => ({
                                                displayName: type
                                                    .split("_")
                                                    .map((word) => {
                                                        // Special case: "llc" should be displayed as "LLC"
                                                        if (word.toLowerCase() === "llc") {
                                                            return "LLC";
                                                        }
                                                        return word.charAt(0).toUpperCase() + word.slice(1);
                                                    })
                                                    .join(" "),
                                                value: type,
                                            }))
                                        ]}
                                        selected={businessType === null ? {
                                            displayName: "Select Business Type",
                                            value: "",
                                        } : {
                                            displayName: businessType
                                                .split("_")
                                                .map((word) => {
                                                    // Special case: "llc" should be displayed as "LLC"
                                                    if (word.toLowerCase() === "llc") {
                                                        return "LLC";
                                                    }
                                                    return word.charAt(0).toUpperCase() + word.slice(1);
                                                })
                                                .join(" "),
                                            value: businessType,
                                        }}
                                        onChange={(value: string) => {
                                            if (value === "") {
                                                setBusinessType(null);
                                            } else {
                                                setBusinessType(value as CheckBusinessType);
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <Label 
                                    required
                                    invalid={!!phone && phone.trim().length > 0 && !isValidPhone(phone.trim())}
                                    invalidReason="Please enter a valid phone number (###-###-####)"
                                >
                                    Phone
                                </Label>
                                <div className="app-custom-property-value">
                                    <input 
                                        type="text"
                                        placeholder="###-###-####"
                                        value={phone}
                                        maxLength={12}
                                        onChange={(e) => {
                                            const formatted = formatPhoneNumber(e.target.value);
                                            setPhone(formatted);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <Label 
                            required
                            invalid={!!federalEIN && federalEIN.trim().length > 0 && !isValidFederalEIN(federalEIN.trim())}
                            invalidReason="Please enter a valid Federal EIN (##-#######)"
                        >
                            Federal EIN
                        </Label>
                        <div className="app-custom-property-value">
                            <input 
                                type="text"
                                placeholder="##-#######"
                                value={federalEIN}
                                maxLength={10}
                                onChange={(e) => {
                                    const formatted = formatFederalEIN(e.target.value);
                                    setFederalEIN(formatted);
                                }}
                            />
                        </div>

                        <Label 
                            required 
                            invalid={!!newCompany?.website && newCompany.website.trim().length > 0 && !isValidURL(newCompany.website.trim())} 
                            invalidReason="Please enter a valid website URL"
                        >
                            Website
                        </Label>
                        <div className="app-custom-property-value">
                            <input 
                                type="text"
                                placeholder="Enter company website"
                                value={newCompany.website || ""}
                                onChange={(e) => {
                                    const websiteValue = e.target.value;
                                    setNewCompany({ ...newCompany, website: websiteValue.trim() });
                                }}
                            />
                        </div>

                        <Label required>
                            Address
                        </Label>
                        <div className="app-custom-property-value">
                            <AppAddress
                                value={address}
                                onChange={(newAddress) => {
                                    setAddress(newAddress);
                                }}
                            />
                        </div>

                        <Label 
                            required
                            invalid={!!firstPayrollDateCF && !isValidPayrollStartDate(firstPayrollDateCF)}
                            invalidReason="Start date must be the 15th or last day of a month (adjusted to Friday if weekend)"
                        >
                            Start Date (no weekends or federal holidays)
                        </Label>
                        <div className="app-custom-property-value">
                            <DatePicker
                                selected={firstPayrollDateCF}
                                dateFormat={"MM/dd/yyyy"}
                                onChange={(date) => {
                                    setFirstPayrollDateCF(date as Date | null);
                                }}
                                minDate={new Date()}
                                filterDate={filterDate}
                                className="date-picker-input"
                                wrapperClassName="app-date-picker-wrapper"
                            />
                        </div>

                        <Button
                            label="Enroll"
                            onClick={handleCreateCompany}
                            disabled={
                                isProcessing || !newCompany.legalName || 
                                !address.line1 || !address.city || 
                                !address.state || !address.postal_code || 
                                !isValidPostalCode(address.postal_code) ||
                                !firstPayrollDateCF || !isValidPayrollStartDate(firstPayrollDateCF) || 
                                !isValidURL(newCompany.website || "") ||
                                !businessType || !phone || !isValidPhone(phone.trim()) ||
                                !federalEIN || !isValidFederalEIN(federalEIN.trim())
                            }
                        />
                    </div>
                    
                </div>
                    
            }

            {state === "signatories" &&
                <AppPaySignatoryNew
                    checkCompany={checkCompany}
                    onSignatoryCreated={handleSignatoryCreated}
                    onError={handleSignatoryError}
                />
            }

            {state === "bank-authorization" &&
                <AppPayBankAccountAuthorization
                    checkCompanyId={checkCompany?.id}
                    checkSignatory={checkSignatories && checkSignatories.length > 0 ? checkSignatories[0] : null}
                    onComplete={async () => {
                        // Refresh checkCompany after bank authorization is complete
                        if (workspace?.company?.checkCompanyId) {
                            const apiResponseCompany = await apiCheckGetCompany(workspace.company.checkCompanyId);
                            if (apiResponseCompany?.data) {
                                setCheckCompany(apiResponseCompany.data);
                            }
                        }
                    }}
                    onError={(error) => {
                        setCheckResponseError(error);
                    }}
                />
            }

            {state === "bank-accounts" &&
                <AppPayBankAccount
                    bankAccount={null}
                    checkCompanyId={checkCompany?.id}
                    onCreate={handleCreateBankAccount}
                    onError={(error) => {setCheckResponseError(error)}}
                />
            }

            {state === "setup-parameters" &&
                <AppPayTaxParameterSetup
                    checkCompany={checkCompany}
                    checkTaxParameters={checkTaxParameters}
                    loadingTaxParameters={loadingTaxParameters}
                    workspace={workspace}
                    onEditTaxParameter={setEditingTaxParameter}
                    onCheckCompanyUpdated={setCheckCompany}
                    onError={setCheckResponseError}
                />
            }

            {state === "filing-authorization" &&
                <AppPayFilingAuthorization
                    checkCompany={checkCompany}
                    workspace={workspace}
                    checkSignatories={checkSignatories}
                    loadingFilingAuthorization={loadingFilingAuthorization}
                    // onViewForm={handleViewForm}
                    // onEditForm={handleOpenForm}
                    // onEmbedForm={handleEmbedFilingAuthorization}
                    // onCheckCompanyUpdated={setCheckCompany}
                    onComplete={handleFilingAuthorizationComplete}
                    onError={setCheckResponseError}
                />
            }

            {state === "onboarding" &&
               <div className="app-pay-enroll-embed">
                    <CheckEmbeddedComponentV2
                        url={checkOnboardLink || ""}
                        title="Company Onboarding"
                        onEvent={(event, data) => {
                            console.log("[AppPayEnroll] Check component event:", event, data);
                            if (event === "check-onboard-app-completed") {
                                navigate("/app/pay-configuration");
                            }
                        }}
                    />
                </div>
            }

            {/* Tax Parameter Edit Modal */}
            <ModalTaxParameterEdit
                isOpen={editingTaxParameter !== null}
                taxParameter={editingTaxParameter}
                onClose={() => setEditingTaxParameter(null)}
                onSave={handleSaveTaxParameter}
            />

            {/* Filing Authorization Form Modal */}
            <ModalCheckFormEditor
                isOpen={editingFilingAuthorization !== null}
                checkCompany={checkCompany || null}
                checkSignatory={checkSignatories && checkSignatories.length > 0 ? checkSignatories[0] : null}
                ein={federalEIN || null}
                filingAuthorization={editingFilingAuthorization}
                onClose={handleCloseFormModal}
                onSuccess={handleFormSubmitSuccess}
            />

            {/* Filing Authorization View Form Modal */}
            <ModalCheckFormViewer
                isOpen={viewingFilingAuthorization !== null}
                filingAuthorization={viewingFilingAuthorization}
                checkCompany={checkCompany || null}
                checkSignatory={checkSignatories && checkSignatories.length > 0 ? checkSignatories[0] : null}
                ein={federalEIN || null}
                onClose={handleCloseViewFormModal}
            />

            {/* Filing Authorization Embed Modal */}
            <ModalCheckCompanyFiling
                isOpen={embeddingFilingAuthorization}
                checkCompany={checkCompany || null}
                checkSignatories={checkSignatories}
                onClose={handleCloseEmbedModal}
            />
        </div>
    );
};
